<template>
  <section v-if="entry?.data && !error && !internalError" :class="sectionClasses">
    <div v-if="title" class="flex items-center mx-auto mb-4 max-w-screen-2xl md:mb-5">
      <ContentfulImage v-if="icon" :entry="icon" class="w-6 h-6 mr-2" />
      <CHeading :id="title" :level="3" class="text-primary-900">{{ title }}</CHeading>
    </div>
    <div :class="{ 'mx-auto max-w-screen-2xl': isBigOrBusinessAreaPage }">
      <ContentfulImage
        v-if="contentTypeId() === 'imageSingle'"
        :entry="entry?.data?.fields?.imageSingle"
        :use-light-box="true"
      />
      <!-- Uses GraphQL could be changed later to component:is -->
      <TextAndMedia v-else-if="contentTypeId() === 'textAndMedia'" :id="id" />
      <InfoBlock v-else-if="contentTypeId() === 'infoBlock'" :id="id" />
      <!-- Uses GraphQL could be changed later to only TextBock and component:is but must handle the extra my-6 -->
      <ContentPageTextBlock
        v-else-if="contentTypeId() === 'textBlock'"
        :id="id"
        :class="{ 'my-6': !isBusinessAreaPage }"
      />
      <SelectedProducts v-else-if="contentTypeId() === 'selectedProducts'" :content="entry?.data" />
      <component :is="getComponent(contentTypeId())" v-else :content="entry?.data" @error="handleInternalError" />
    </div>
  </section>
  <div v-else />
</template>

<script setup lang="ts">
import {
  AccordionsContainer,
  AssortmentIcons,
  LargeSection,
  RandomBanners,
  ContactCards,
  ContentBanners,
  TextBlock,
  MarketingBanners,
  AccountApplication,
  SelectedProducts,
  ServiceCards,
  SubBanners,
  ImageSetOfThree,
  Video,
  InfoBlock,
  TextAndMedia,
} from '#components';
import { getContentTypeId, getDefaultSectionClass, getSmallSectionClass, isSmallSection } from '~/utils/SectionSize';

type Props = {
  section: any;
  id: string;
  contentType: string;
  isBusinessAreaPage?: boolean;
};

type Components = {
  [key: string]: any;
};

const props = withDefaults(defineProps<Props>(), {
  section: {},
  isBusinessAreaPage: false,
});

const components: Components = {
  accordions: AccordionsContainer,
  accountApplication: AccountApplication,
  assortmentIcons: AssortmentIcons,
  contactCards: ContactCards,
  contentBanners: ContentBanners,
  largeSection: LargeSection,
  marketingBanners: MarketingBanners,
  randomBanners: RandomBanners,
  textBlock: TextBlock,
  selectedProducts: SelectedProducts,
  serviceCards: ServiceCards,
  subBanners: SubBanners,
  imageSetOfThree: ImageSetOfThree,
  video: Video,
  infoBlock: InfoBlock,
  textAndMedia: TextAndMedia,
};

const { fetchEntry } = useContentful();

const contentTypeId = () => getContentTypeId(props.contentType);

const { data: entry } = await useAsyncData(
  props.id,
  async () =>
    await fetchEntry({
      contentType: contentTypeId(),
      'sys.id[in]': props.id,
      include: contentTypeId() === 'serviceCards' ? 2 : 1,
    }),
);

const error = ref(entry.value?.error);
const internalError = ref(false);

const icon = computed(() => props.section?.fields?.icon || props.section?.icon);
const title = computed(() => props.section?.fields?.title || props.section?.title);

const backgroundColorClass = computed(() => {
  const backgroundColor = props.section.fields?.backgroundColor || props.section?.backgroundColor;
  if (backgroundColor === 'beige-500') {
    return 'bg-beige-500';
  }
  if (backgroundColor === 'white') {
    return 'bg-white';
  }
  return null;
});

const handleInternalError = () => {
  internalError.value = true;
};

const getComponent = (component: string) => {
  return components[component];
};

const isBigOrBusinessAreaPage = computed(() => {
  return !isSmallSection(props.contentType) || props.isBusinessAreaPage;
});

const sectionClasses = computed(() => {
  if (isSmallSection(props.contentType) && !props.isBusinessAreaPage) {
    return getSmallSectionClass();
  }

  return getColoredDefaultSectionClass(getDefaultSectionClass(), backgroundColorClass.value);
});

function getColoredDefaultSectionClass(defaultClass: string, color?: 'bg-white' | 'bg-beige-500' | null): string {
  if (color) {
    return [defaultClass, color].join(' ');
  }

  return defaultClass;
}
</script>

<script lang="ts">
export default defineComponent({
  name: 'SectionComponent',
});
</script>
